import { motion } from 'motion/react';
import styles from './MissionCard.module.scss';

interface MissionCardProps {
  icon: 'book' | 'brain' | 'lightbulb';
  title: string;
  description: string;
  delay?: number;
}

export function MissionCard({ icon, title, description, delay = 0 }: MissionCardProps) {
  const getIconContent = () => {
    switch (icon) {
      case 'book':
        return (
          <svg viewBox="0 0 48 48" className={styles.missionCard__iconSvg}>
            <motion.g
              initial={{ rotateY: 0 }}
              whileHover={{ rotateY: -20 }}
              transition={{ type: 'spring', stiffness: 200 }}
            >
              {/* Book spine */}
              <rect x="8" y="8" width="4" height="32" rx="1" fill="var(--forest-500)" />
              {/* Book cover */}
              <motion.rect
                x="12" y="8" width="28" height="32" rx="2"
                fill="var(--forest-400)"
                initial={{ originX: 0 }}
              />
              {/* Pages */}
              <rect x="14" y="10" width="24" height="28" rx="1" fill="var(--card-bg)" />
              {/* Page lines */}
              <line x1="18" y1="16" x2="34" y2="16" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="18" y1="22" x2="34" y2="22" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeLinecap="round" />
              <line x1="18" y1="28" x2="30" y2="28" stroke="var(--text-tertiary)" strokeWidth="1.5" strokeLinecap="round" />
            </motion.g>
          </svg>
        );
      case 'brain':
        return (
          <svg viewBox="0 0 48 48" className={styles.missionCard__iconSvg}>
            <g>
              {/* Brain outline */}
              <path
                d="M24 8 C32 8 38 14 38 22 C38 26 36 30 32 33 L32 38 C32 39 31 40 30 40 L18 40 C17 40 16 39 16 38 L16 33 C12 30 10 26 10 22 C10 14 16 8 24 8Z"
                fill="var(--ocean-400)"
                stroke="var(--ocean-600)"
                strokeWidth="1.5"
              />
              {/* Neural connections - symmetric around center (24, 22) */}
              <g>
                <circle cx="17" cy="15" r="2" fill="var(--sunset-400)" />
                <circle cx="31" cy="15" r="2" fill="var(--sunset-400)" />
                <circle cx="24" cy="22" r="2.5" fill="var(--sunset-500)" />
                <circle cx="17" cy="28" r="2" fill="var(--sunset-400)" />
                <circle cx="31" cy="28" r="2" fill="var(--sunset-400)" />
                <line x1="17" y1="15" x2="24" y2="22" stroke="var(--sunset-300)" strokeWidth="1" />
                <line x1="31" y1="15" x2="24" y2="22" stroke="var(--sunset-300)" strokeWidth="1" />
                <line x1="24" y1="22" x2="17" y2="28" stroke="var(--sunset-300)" strokeWidth="1" />
                <line x1="24" y1="22" x2="31" y2="28" stroke="var(--sunset-300)" strokeWidth="1" />
              </g>
            </g>
          </svg>
        );
      case 'lightbulb':
        return (
          <svg viewBox="0 0 48 48" className={styles.missionCard__iconSvg}>
            <motion.g
              animate={{ filter: ['drop-shadow(0 0 4px var(--sunset-400))', 'drop-shadow(0 0 12px var(--sunset-400))', 'drop-shadow(0 0 4px var(--sunset-400))'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              {/* Bulb */}
              <path
                d="M24 6 C32 6 38 12 38 20 C38 26 34 30 30 34 L30 38 C30 40 28 42 26 42 L22 42 C20 42 18 40 18 38 L18 34 C14 30 10 26 10 20 C10 12 16 6 24 6Z"
                fill="var(--sunset-300)"
                stroke="var(--sunset-500)"
                strokeWidth="1.5"
              />
              {/* Filament lines */}
              <line x1="20" y1="42" x2="28" y2="42" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round" />
              <line x1="21" y1="45" x2="27" y2="45" stroke="var(--text-tertiary)" strokeWidth="2" strokeLinecap="round" />
              {/* Light rays */}
              <motion.g
                animate={{ opacity: [0.3, 0.8, 0.3] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
              >
                <line x1="24" y1="0" x2="24" y2="3" stroke="var(--sunset-400)" strokeWidth="2" strokeLinecap="round" />
                <line x1="6" y1="20" x2="3" y2="20" stroke="var(--sunset-400)" strokeWidth="2" strokeLinecap="round" />
                <line x1="42" y1="20" x2="45" y2="20" stroke="var(--sunset-400)" strokeWidth="2" strokeLinecap="round" />
                <line x1="10" y1="8" x2="8" y2="6" stroke="var(--sunset-400)" strokeWidth="2" strokeLinecap="round" />
                <line x1="38" y1="8" x2="40" y2="6" stroke="var(--sunset-400)" strokeWidth="2" strokeLinecap="round" />
              </motion.g>
            </motion.g>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div
      className={styles.missionCard}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay,
        type: 'spring',
        stiffness: 100,
        damping: 15
      }}
    >
      <div className={styles.missionCard__icon}>
        {getIconContent()}
      </div>
      <h3 className={styles.missionCard__title}>{title}</h3>
      <p className={styles.missionCard__description}>{description}</p>
    </motion.div>
  );
}
